import { clerkAuthHeaders } from './clerkApiAuth';
import { AppEvent, AppEventDetail, EventWriteInput, StaffEvent, StaffEventSummary } from '../types/event';

const API_BASE_URL = '/api';

async function errorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json();
    if (typeof body?.error === 'string' && body.error.trim()) return body.error;
  } catch {
    // The body was not JSON.
  }
  return fallback;
}

export const eventAPI = {
  async fetchEvents(): Promise<AppEvent[]> {
    const response = await fetch(`${API_BASE_URL}/events`);
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to load events'));
    return response.json();
  },

  async fetchEventBySlug(slug: string): Promise<AppEventDetail> {
    const response = await fetch(`${API_BASE_URL}/events/slug/${encodeURIComponent(slug)}`);
    if (!response.ok) throw new Error(await errorMessage(response, 'Event not found'));
    return response.json();
  },

  async fetchStaffEvents(): Promise<StaffEventSummary[]> {
    const response = await fetch(`${API_BASE_URL}/events?includeDrafts=true`, {
      headers: await clerkAuthHeaders({ json: false }),
    });
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to load events'));
    return response.json();
  },

  async fetchStaffEvent(id: string): Promise<StaffEvent> {
    const response = await fetch(`${API_BASE_URL}/events/${encodeURIComponent(id)}`, {
      headers: await clerkAuthHeaders({ json: false }),
    });
    if (!response.ok) throw new Error(await errorMessage(response, 'Event not found'));
    return response.json();
  },

  async createEvent(input: EventWriteInput): Promise<StaffEvent> {
    const response = await fetch(`${API_BASE_URL}/events`, {
      method: 'POST',
      headers: await clerkAuthHeaders(),
      body: JSON.stringify(input),
    });
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to create event'));
    return response.json();
  },

  async updateEvent(id: string, input: EventWriteInput): Promise<StaffEvent> {
    const response = await fetch(`${API_BASE_URL}/events/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: await clerkAuthHeaders(),
      body: JSON.stringify(input),
    });
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to update event'));
    return response.json();
  },

  async deleteEvent(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/events/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: await clerkAuthHeaders({ json: false }),
    });
    if (!response.ok) throw new Error(await errorMessage(response, 'Failed to delete event'));
  },
};
